import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../../../utils/userStore';
import { micIcon, studyModeIcon, plusIconV2, Send } from '../../../assets/images/index';
import './chat-input-tool.scss';
// Removed redundant SVG component imports

export interface ChatInputToolProps {
  onSendMessage: (msg: string) => void;
  onMicClick: () => void;
  onAttachClick?: () => void;
  onStudyModeClick: () => void;
  showAttach?: boolean;
  highlightMic?: boolean;
  highlightInput?: boolean;
  highlightAttach?: boolean;
  highlightStudyMode?: boolean;
  placeholder?: string;
  studyIcon?: React.ReactNode;
  studyHighlightVariant?: 'orange' | 'dark';
  isLoading?: boolean;
  disabled?: boolean;
}

export const ChatInputTool: React.FC<ChatInputToolProps> = ({
  onSendMessage,
  onMicClick,
  onAttachClick,
  onStudyModeClick,
  showAttach = false,
  highlightMic,
  highlightInput,
  highlightAttach,
  highlightStudyMode,
  placeholder,
  studyIcon,
  studyHighlightVariant = 'orange',
  isLoading = false,
  disabled = false
}) => {
  const { t, i18n } = useTranslation();
  const { user_attributes } = useUserStore();
  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const gender = user_attributes.gender ? Number(user_attributes.gender) : null;
  const genderSuffix = gender === 1 ? '_male' : (gender === 2 ? '_female' : '');
  const placeholderKey = 'chatInputPlaceHolder';
  const genderedPlaceholderKey = `${placeholderKey}${genderSuffix}`;
  
  const resolvedPlaceholder = placeholder || (i18n.exists(genderedPlaceholderKey) ? t(genderedPlaceholderKey) : t(placeholderKey));

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 50); // Allow it to grow up to 120px
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading && !disabled) {
      onSendMessage(inputValue);
      setInputValue('');
      if (textareaRef.current) {
        textareaRef.current.style.height = '40px';
      }
    }
  };

  const getStudyBtnClass = () => {
    let base = 'v2-chat-input-tool__btn v2-chat-input-tool__btn--study';
    if (highlightStudyMode) {
      const variantClass = studyHighlightVariant === 'dark'
        ? 'v2-chat-input-tool__btn--study-highlight-dark'
        : 'v2-chat-input-tool__btn--study-highlight-orange';
      base += ` ${variantClass}`;
    }
    return base;
  };

  return (
    <div className="v2-chat-input-tool">
      <form className="v2-chat-input-tool__form" onSubmit={handleSubmit}>
        <div className={`v2-chat-input-tool__input-wrapper ${highlightInput ? 'v2-chat-input-tool__input-wrapper--highlight' : ''}`}>
          {showAttach && (
            <button
              type="button"
              className={`v2-chat-input-tool__btn-attach ${highlightAttach ? 'v2-chat-input-tool__btn-attach--highlight' : ''}`}
              onClick={onAttachClick}
            >
              <img src={plusIconV2} alt="Attach" />
            </button>
          )}
          <textarea
            ref={textareaRef}
            className={`v2-chat-input-tool__input ${disabled ? 'disabled' : ''}`}
            placeholder={resolvedPlaceholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            rows={1}
            disabled={disabled}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Let the default behavior (new line) happen
                // We don't call handleSubmit here anymore as per user request
              }
            }}
          />
        </div>

        <div className="v2-chat-input-tool__actions-left">
          <button
            type="button"
            className={getStudyBtnClass()}
            onClick={onStudyModeClick}
            disabled={disabled}
          >
            {studyIcon || <img src={studyModeIcon} alt="Study" />}
          </button>
          <button
            type={inputValue.trim() ? "submit" : "button"}
            className={`v2-chat-input-tool__btn v2-chat-input-tool__btn--mic ${highlightMic ? 'v2-chat-input-tool__btn--mic-highlight' : ''} ${isLoading || disabled ? 'disabled' : ''}`}
            onClick={inputValue.trim() ? undefined : onMicClick}
            disabled={isLoading || disabled}
          >
            <img src={inputValue.trim() ? Send : micIcon} alt={inputValue.trim() ? "send" : "mic"} />
          </button>
        </div>
      </form>
    </div>
  );
};



export default ChatInputTool;
